import React, {useMemo} from 'react';
import {List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Tooltip,} from '@mui/material';
import {useChatContext} from "../context/ChatContext";
import type {ChatSession} from "../types/chatTypes";
import moment from "moment";
import LightEditSquare from '../../../components/icons/svg/LightEditSquare.svg?react';

type SessionGroup = {
    label: string;
    sessions: ChatSession[];
};

const groupSessionsByDate = (sessions: ChatSession[]): SessionGroup[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: Map<string, ChatSession[]> = new Map();

    // Sort sessions by updated_at descending (most recent first)
    const sortedSessions = [...sessions].sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());
    for (const session of sortedSessions) {
        const sessionDate = new Date(session.updated_at.getFullYear(), session.updated_at.getMonth(), session.updated_at.getDate());

        let groupLabel: string;
        if (sessionDate.getTime() === today.getTime()) {
            groupLabel = 'Today';
        } else if (sessionDate.getTime() === yesterday.getTime()) {
            groupLabel = 'Yesterday';
        } else {
            // Format as "Monday, 23 Jan 2023" (in appropriate locale)
            const m = moment(session.updated_at);
            groupLabel = `${m.format('dddd')}, ${m.format('ll')}`
        }
        if (!groups.has(groupLabel)) {
            groups.set(groupLabel, []);
        }
        groups.get(groupLabel)!.push(session);
    }

    // Convert map to array while preserving order
    return Array.from(groups.entries()).map(([label, sessions]) => ({
        label,
        sessions,
    }));
};

export const Sidebar: React.FC = () => {
    const {
        sessions,
        currentSession,
        selectSession,
        startNewChat,
    } = useChatContext();
    const groupedSessions = useMemo(() => groupSessionsByDate(sessions), [sessions]);

    return <div className="sidebar">
        {/* Header */}
        <List className="sidebar-actions" disablePadding>
            <ListItemButton onClick={() => startNewChat()}>
                <ListItemIcon><LightEditSquare/></ListItemIcon>
                <ListItemText primary="New chat"/>
            </ListItemButton>
        </List>

        {/* Session List */}
        <List className="sidebar-items" disablePadding>
            {groupedSessions.map((group, i) => <React.Fragment key={group.label}>
                <ListSubheader className={i === 0 ? 'first' : ''}>{group.label}</ListSubheader>
                {group.sessions.map((session) => (
                    <ListItem key={session.id} disablePadding>
                        <Tooltip
                            title={moment(session.updated_at).format('LLLL')}
                            placement="right"
                            arrow>
                            <ListItemButton
                                selected={currentSession?.id === session.id}
                                onClick={() => selectSession(session)}
                                className={`session-item ${currentSession?.id === session.id ? 'selected' : ''}`}>
                                <ListItemText primary={session.name}/>
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}
            </React.Fragment>)}
        </List>
    </div>
};
